import React, { createContext, useContext, useState, useEffect } from 'react';
import { Gift } from '../services/giftApi';

interface GiftCartItem {
    gift: Gift;
    quantity: number;
}

interface GiftCartContextType {
    cart: GiftCartItem[];
    addToCart: (gift: Gift) => void;
    decrementQuantity: (giftId: string) => void;
    removeFromCart: (giftId: string) => void;
    clearCart: () => void;
    totalItems: number;
    isCartOpen: boolean;
    setIsCartOpen: (open: boolean) => void;
    isCheckoutOpen: boolean;
    setIsCheckoutOpen: (open: boolean) => void;
}

const GiftCartContext = createContext<GiftCartContextType | undefined>(undefined);

export const GiftCartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [cart, setCart] = useState<GiftCartItem[]>([]);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

    // Load cart from localStorage on mount
    useEffect(() => {
        const savedCart = localStorage.getItem('gift_cart');
        if (savedCart) {
            try {
                setCart(JSON.parse(savedCart));
            } catch (e) {
                console.error('Failed to parse gift cart', e);
            }
        }
    }, []);

    // Save cart to localStorage on change
    useEffect(() => {
        localStorage.setItem('gift_cart', JSON.stringify(cart));
        // Dispatch event for other components (like NavigationContext if needed)
        window.dispatchEvent(new Event('giftCartUpdated'));
    }, [cart]);

    const addToCart = (gift: Gift) => {
        setCart(prev => {
            const existing = prev.find(item => item.gift.id === gift.id);
            if (existing) {
                return prev.map(item => item.gift.id === gift.id ? { ...item, quantity: item.quantity + 1 } : item);
            }
            return [...prev, { gift, quantity: 1 }];
        });
    };

    const decrementQuantity = (giftId: string) => {
        setCart(prev => {
            const existing = prev.find(item => item.gift.id === giftId);
            if (existing && existing.quantity > 1) {
                return prev.map(item => item.gift.id === giftId ? { ...item, quantity: item.quantity - 1 } : item);
            }
            return prev.filter(item => item.gift.id !== giftId);
        });
    };

    const removeFromCart = (giftId: string) => {
        setCart(prev => prev.filter(item => item.gift.id !== giftId));
    };

    const clearCart = () => {
        setCart([]);
    };

    const totalItems = cart.reduce((acc, item) => acc + item.quantity, 0);

    return (
        <GiftCartContext.Provider value={{ 
            cart, 
            addToCart, 
            decrementQuantity, 
            removeFromCart, 
            clearCart, 
            totalItems,
            isCartOpen,
            setIsCartOpen,
            isCheckoutOpen,
            setIsCheckoutOpen
        }}>
            {children}
        </GiftCartContext.Provider>
    );
};

export const useGiftCart = () => {
    const context = useContext(GiftCartContext);
    if (context === undefined) {
        throw new Error('useGiftCart must be used within a GiftCartProvider');
    }
    return context;
};
